import type { ActivityOf } from '@wyn/content';
import { BTN, type LockpickingSim, lockpickingSim } from '@wyn/game-core';
import { Progress, Stage } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { useRealtime } from './useRealtime.ts';

const KEYS = { Space: BTN.main, ArrowUp: BTN.main, ArrowLeft: BTN.left, ArrowRight: BTN.right };

/** Locksmiths' Row: slide the pick until it trembles, then lift the pin. */
export function LockpickingGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'lockpicking'>;
  const rt = useRealtime<LockpickingSim>({
    create: () => lockpickingSim(a, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
    keys: KEYS,
  });
  const sim = rt.sim;
  if (!sim) return null;
  const shake = sim.feel * (sim.tick % 2 ? 1 : -1);
  const btn = (held: boolean) =>
    `select-none touch-none border-2 py-4 text-lg transition-colors ${held ? 'border-gilt bg-gilt text-vellum' : 'border-gilt/70 text-gilt'} disabled:opacity-40`;

  return (
    <Stage fx={rt.fx}>
      <p className="text-sm text-ink-dim">
        Hold ◀ ▶ (or the arrow keys) to slide the pick. The <b>Feel</b> bar and the pick’s trembling grow as you near the sweet spot. Then <b>Lift</b> (Space or ↑). A wrong lift strains the pick. Finish before the watchman’s lantern burns out.
        {a.security ? <> Some pins here are <b>security pins</b>: a false set feels just as strong, but its feel <b>throbs</b>. The true spot holds steady.</> : null}
      </p>
      <div className="flex h-16 items-end justify-center gap-2" aria-label={`Pins set ${sim.pin} of ${sim.pins}`}>
        {Array.from({ length: sim.pins }, (_, i) => (
          <div
            key={i}
            className={`h-10 w-4 rounded-t transition-transform duration-200 ${i < sim.pin ? '-translate-y-3.5 bg-gilt' : 'bg-[#6b665c]'} ${i === sim.pin ? 'outline-2 outline-gilt' : ''}`}
          />
        ))}
      </div>
      <div className="relative h-9 rounded-full border border-rule bg-[#2b2823]">
        <div
          className="absolute top-1 h-[26px] w-1.5 -ml-[3px] rounded bg-[#cfd3d6] shadow-[0_0_6px_#9ab]"
          style={{ left: `${sim.pick}%`, transform: `translate(${shake * 2.5}px, ${shake * 1.5}px)` }}
        />
      </div>
      <Progress value={sim.feel} label="Feel" />
      <Progress value={Math.min(1, sim.strain / 100)} label="Strain on the pick" />
      <Progress value={Math.max(0, sim.timeLeft / a.time)} label="Lantern" />
      <div className="grid grid-cols-3 gap-2">
        <button type="button" aria-label="Slide left" className={btn(rt.isHeld(BTN.left))} {...rt.holdProps(BTN.left)} disabled={!!sim.result}>◀</button>
        <button type="button" className={btn(rt.isHeld(BTN.main))} {...rt.holdProps(BTN.main)} disabled={!!sim.result}>Lift</button>
        <button type="button" aria-label="Slide right" className={btn(rt.isHeld(BTN.right))} {...rt.holdProps(BTN.right)} disabled={!!sim.result}>▶</button>
      </div>
    </Stage>
  );
}
